import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import styles from './InsightCommerce.module.scss';
import homeStyles from '../../../styles/Home.module.scss';

interface Feature {
  label: string;
  icon: React.ReactNode;
}

const features: Feature[] = [
  {
    label: 'Semantic and Behavioural insights',
    icon: <path d="M3 12h4l3 7 4-15 3 8h4" />,
  },
  {
    label: 'Geographical insights',
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
      </>
    ),
  },
  {
    label: 'Marketing insights',
    icon: (
      <>
        <path d="M3 10v4l13 5V5z" />
        <path d="M16 9a3.5 3.5 0 0 1 0 6" />
      </>
    ),
  },
  {
    label: 'Customer insights',
    icon: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </>
    ),
  },
  {
    label: 'Revenues and Footfall insights',
    icon: (
      <>
        <path d="M3 21h18" />
        <rect x="5" y="11" width="3" height="7" rx="0.5" />
        <rect x="10.5" y="6" width="3" height="12" rx="0.5" />
        <rect x="16" y="9" width="3" height="9" rx="0.5" />
      </>
    ),
  },
  {
    label: 'Purchase frequency insights',
    icon: (
      <>
        <path d="M4 9a8 8 0 0 1 14-4l2 2" />
        <path d="M20 15a8 8 0 0 1-14 4l-2-2" />
        <path d="M18 3v4h-4M6 21v-4h4" />
      </>
    ),
  },
  {
    label: 'Competitor insights',
    icon: (
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
  },
];

const InsightCommerce = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (event: React.MouseEvent<HTMLLIElement>) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    el.style.setProperty('--my', `${event.clientY - rect.top}px`);
  };

  return (
    <div
      ref={sectionRef}
      className={`${styles['new-container']} ${
        revealed ? styles['revealed'] : ''
      }`}
    >
      <div className={styles['left-col']}>
        <div className={styles['mockup-wrapper']}>
          <span className={styles['glow']} aria-hidden="true" />
          <Image
            width={816}
            height={616}
            src="https://minio.socommerz.in/b2c/FILE/217dc3c1-a843-496d-a727-4ac256b2c161.png"
            alt="Insight Commerce"
            className={styles['tablet-img']}
          />
        </div>
      </div>

      <div className={styles['right-col']}>
        <p className={styles['eyebrow']}>Business insights platform</p>
        <h2 className={styles['main-heading']}>
          Insight Commerce for
          <br />
          <span className={homeStyles['span-gradient']}>
            Premium Global Retailers
          </span>
        </h2>
        <p className={styles['subtext']}>
          Unlock deep retail intelligence with our comprehensive insights suite.
          Analyze customer behavior, monitor footfall, and track competitors to
          make data-driven decisions that grow your business.
        </p>

        <ul className={styles['feature-list']}>
          {features.map((item, index) => (
            <li
              key={index}
              className={styles['feature-item']}
              onMouseMove={handleMouseMove}
              style={{ '--i': index } as React.CSSProperties}
            >
              <span className={styles['item-glow']} aria-hidden="true" />
              <span className={styles['check-icon']}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {item.icon}
                </svg>
              </span>
              <span className={styles['feature-text']}>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default InsightCommerce;
